import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  BookOpen,
  LayoutDashboard,
  Box,
  Warehouse,
  ShoppingCart,
  BarChart3,
  Users,
  Settings,
  FileText,
  Scan,
  ArrowRight,
  CheckCircle2,
  PlayCircle,
  Download,
  Truck,
  ShoppingBag,
  MapPin,
  CreditCard,
  ArrowLeftRight,
  Layers,
  Package,
  Receipt,
  AlertTriangle,
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  Printer
} from 'lucide-react';

export default function TrainingManualPage() {
  const modules = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      description: 'Your business overview at a glance',
      image: '/training/dashboard.png',
      steps: [
        { step: 'View KPIs', description: 'See total items, warehouses, low stock alerts, and inventory value' },
        { step: 'Today\'s Sales', description: 'Monitor daily sales count, revenue, profit, and units sold' },
        { step: 'Recent Activity', description: 'Track latest stock movements and transactions' },
        { step: 'Quick Actions', description: 'Access frequently used features directly from dashboard' }
      ]
    },
    {
      id: 'items',
      title: 'Items Management',
      icon: Box,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      description: 'Add and manage your products',
      image: '/training/items.png',
      steps: [
        { step: 'Add New Item', description: 'Click "Add Item" button → Enter SKU, name, cost price, selling price, starting quantity → Save', image: '/training/add-item.png' },
        { step: 'Edit Item', description: 'Click the pencil icon on any item → Modify details → Set Stock Quantity → Save' },
        { step: 'Set Reorder Level', description: 'Set minimum stock level to receive low stock alerts' },
        { step: 'Generate Barcode', description: 'Click barcode icon → Print barcode label for the item' },
        { step: 'Import Items (CSV)', description: 'Click Import → Upload CSV with: SKU, Name, Category, Cost Price, Selling Price, Quantity' },
        { step: 'Export Items', description: 'Click Export to download all items as CSV file' }
      ]
    },
    {
      id: 'warehouses',
      title: 'Warehouses',
      icon: Warehouse,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      description: 'Manage storage locations',
      image: '/training/warehouses.png',
      steps: [
        { step: 'Add Warehouse', description: 'Click "Add Warehouse" → Enter code (e.g., WH001), name, address → Save' },
        { step: 'View Warehouse', description: 'Click on warehouse to see all inventory stored there' },
        { step: 'Edit Details', description: 'Update warehouse name, address, or status' },
        { step: 'Deactivate', description: 'Set warehouse as inactive (cannot receive new stock)' }
      ]
    },
    {
      id: 'storage-locations',
      title: 'Storage Locations',
      icon: MapPin,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      description: 'Organize within warehouses',
      steps: [
        { step: 'Create Location', description: 'Select warehouse → Add Zone, Aisle, Rack, Shelf, or Bin' },
        { step: 'Location Hierarchy', description: 'Locations follow: Zone → Aisle → Rack → Shelf → Bin' },
        { step: 'Assign Items', description: 'When receiving stock, specify exact storage location' },
        { step: 'View Path', description: 'See full location path (e.g., "Zone A / Aisle 1 / Rack 2")' }
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory',
      icon: Layers,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      description: 'View stock levels',
      image: '/training/inventory.png',
      steps: [
        { step: 'View All Stock', description: 'See current quantity and value for all items across warehouses' },
        { step: 'Filter by Warehouse', description: 'Select specific warehouse to see its inventory only' },
        { step: 'Search Items', description: 'Use search box to find specific products' },
        { step: 'Check Low Stock', description: 'Items below reorder level are highlighted in red' }
      ]
    },
    {
      id: 'transactions',
      title: 'Stock Transactions',
      icon: ArrowLeftRight,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10',
      description: 'Record stock movements',
      image: '/training/transactions.png',
      steps: [
        { step: 'Goods Receipt (GR)', description: 'Receive stock from supplier → Select item, warehouse, quantity, cost → Creates inventory' },
        { step: 'Goods Issue (GI)', description: 'Issue stock out → Select item, warehouse, quantity, reason → Reduces inventory' },
        { step: 'Transfer (TR)', description: 'Move stock between warehouses → Select from/to warehouse, item, quantity' },
        { step: 'Adjustment (ADJ)', description: 'Correct stock discrepancies → Enter actual quantity and reason' },
        { step: 'Cycle Count (CC)', description: 'Record physical count → System calculates variance automatically' }
      ]
    },
    {
      id: 'sales',
      title: 'Sales',
      icon: ShoppingCart,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      description: 'Process customer sales',
      image: '/training/sales.png',
      steps: [
        { step: 'Start New Sale', description: 'Click "New Sale" to open sales screen' },
        { step: 'Select Customer', description: 'Search customer by name or phone (optional — earns loyalty points)' },
        { step: 'Add Items to Cart', description: 'Search product OR scan barcode → Selling price auto-fills → Enter quantity → Add to cart' },
        { step: 'Scan Barcode', description: 'Click camera icon → Point at barcode → Item auto-added' },
        { step: 'Apply Discount', description: 'Choose percentage or fixed amount discount' },
        { step: 'Enter Amount Paid', description: 'Enter amount received → Change is calculated automatically' },
        { step: 'Complete Sale', description: 'Click "Complete Sale" → Stock deducted, receipt generated, customer points updated' },
        { step: 'Print Receipt', description: 'Receipt shows business name, address, phones, items, return policy' },
        { step: 'View Sales History', description: 'See all past sales with receipt numbers and details' }
      ]
    },
    {
      id: 'purchase-orders',
      title: 'Purchase Orders',
      icon: Truck,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      description: 'Order from suppliers',
      steps: [
        { step: 'Create PO', description: 'Click "New Purchase Order" → Select supplier → Add items and quantities' },
        { step: 'PO Number', description: 'Auto-generated as PO-YYYYMMDD-XXXX' },
        { step: 'Submit PO', description: 'Save to send to supplier' },
        { step: 'Receive PO', description: 'When goods arrive → Click "Receive" → Stock added via Goods Receipt' },
        { step: 'Track Status', description: 'View pending, partial, and completed purchase orders' }
      ]
    },
    {
      id: 'sales-orders',
      title: 'Sales Orders',
      icon: ShoppingBag,
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
      description: 'Manage customer orders',
      steps: [
        { step: 'Create SO', description: 'Click "New Sales Order" → Select customer → Add items' },
        { step: 'SO Number', description: 'Auto-generated as SO-YYYYMMDD-XXXX' },
        { step: 'Fulfill Order', description: 'Click "Fulfill" → Stock issued via Goods Issue' },
        { step: 'Track Status', description: 'Monitor pending, processing, and completed orders' }
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: BarChart3,
      color: 'text-teal-500',
      bgColor: 'bg-teal-500/10',
      description: 'Business insights',
      image: '/training/reports.png',
      steps: [
        { step: 'Stock Valuation', description: 'See total value of inventory by item and warehouse' },
        { step: 'Stock on Hand', description: 'Current quantities across all locations' },
        { step: 'Movement History', description: 'All stock movements with dates and users' },
        { step: 'Profit & Loss', description: 'Sales revenue, costs, and profit margins' },
        { step: 'Export Reports', description: 'Download any report as CSV for Excel analysis' }
      ]
    },
    {
      id: 'users',
      title: 'User Management',
      icon: Users,
      color: 'text-violet-500',
      bgColor: 'bg-violet-500/10',
      description: 'Manage team access',
      steps: [
        { step: 'Add User', description: 'Click "Add User" → Enter email, name, password → Assign role' },
        { step: 'User Roles', description: 'Org Admin (full access), Inventory Manager, Warehouse Staff, Auditor (view only)' },
        { step: 'Edit User', description: 'Change user details or role' },
        { step: 'Deactivate User', description: 'Remove access without deleting history' }
      ]
    },
    {
      id: 'customers',
      title: 'Customers & Loyalty',
      icon: Users,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      description: 'Manage customers and rewards',
      image: '/training/customers.png',
      steps: [
        { step: 'Add Customer', description: 'Click "Add Customer" → Enter name, phone, email → Auto-generates account number (CUS-0001)' },
        { step: 'Search Customers', description: 'Search by name, phone, or account number' },
        { step: 'Link to Sale', description: 'During checkout, search and select customer → Points earned automatically' },
        { step: 'View Profile', description: 'Click customer to see total spent, points, visits, and purchase history' },
        { step: 'Edit Customer', description: 'Click pencil icon to update name, phone, email, notes' },
        { step: 'Points System', description: 'Customers earn 1 point per J$100 spent. Track points for future rewards' }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      color: 'text-gray-500',
      bgColor: 'bg-gray-500/10',
      description: 'Configure your business',
      image: '/training/settings.png',
      steps: [
        { step: 'Business Name', description: 'Enter your business name — appears on all receipts and invoices' },
        { step: 'Address & Contact', description: 'Add address, phone 1, phone 2, and email — shown on receipts' },
        { step: 'Receipt Tagline', description: 'Custom message at the bottom of receipts (e.g. "Thank you for shopping!")' },
        { step: 'Return Policy', description: 'Enter your return policy — each line appears on the receipt' },
        { step: 'Receipt Preview', description: 'See exactly how your receipt will look before printing' }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Subscription',
      icon: CreditCard,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
      description: 'Manage your plan',
      image: '/training/settings.png',
      steps: [
        { step: 'View Current Plan', description: 'See your subscription tier and usage limits' },
        { step: 'Check Usage', description: 'Monitor warehouses, users, and SKUs used vs. limit' },
        { step: 'Upgrade Plan', description: 'Click plan card → Redirected to secure Stripe checkout' },
        { step: 'Payment', description: 'Complete payment via credit/debit card' },
        { step: 'Subscription Active', description: 'Plan activates immediately after payment' }
      ]
    }
  ];

  const quickTips = [
    { tip: 'Use barcode scanning on mobile for faster sales', icon: Scan },
    { tip: 'Set reorder levels to get low stock alerts', icon: AlertTriangle },
    { tip: 'Export data regularly for backup', icon: Download },
    { tip: 'Use the search bar to find items quickly', icon: Search },
    { tip: 'Print receipts for customer records', icon: Printer },
    { tip: 'Install as app on phone for quick access', icon: Package }
  ];

  const handleDownloadPDF = () => {
    // Create printable version
    window.print();
  };

  return (
    <div className="space-y-8 print:space-y-4" data-testid="training-manual-page">
      {/* Header */}
      <div className="flex items-center justify-between print:block">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">TRAINING MANUAL</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Complete guide to using JamInventra inventory system</p>
        </div>
        <Button onClick={handleDownloadPDF} className="bg-[#007AFF] hover:bg-[#0066D6] print:hidden">
          <Download className="w-4 h-4 mr-2" />
          Print / Save PDF
        </Button>
      </div>

      {/* Quick Start */}
      <Card className="border-2 border-[#007AFF] shadow-none bg-[#007AFF]/5 print:border print:bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#007AFF]">
            <PlayCircle className="w-6 h-6" />
            Quick Start Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <p className="font-semibold text-[#0A0A0A]">Add Items</p>
                <p className="text-sm text-[#0A0A0A]/60">Create your product catalog</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <p className="font-semibold text-[#0A0A0A]">Set Up Warehouse</p>
                <p className="text-sm text-[#0A0A0A]/60">Define storage locations</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <p className="font-semibold text-[#0A0A0A]">Receive Stock</p>
                <p className="text-sm text-[#0A0A0A]/60">Use Goods Receipt to add inventory</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-bold text-sm">4</div>
              <div>
                <p className="font-semibold text-[#0A0A0A]">Start Selling</p>
                <p className="text-sm text-[#0A0A0A]/60">Process sales with barcode scanning</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="border border-[#E5E5EA] shadow-none print:break-inside-avoid">
        <CardHeader>
          <CardTitle className="text-lg">💡 Quick Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {quickTips.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-[#0A0A0A]/70">
                <item.icon className="w-4 h-4 text-[#007AFF]" />
                {item.tip}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Module Guides */}
      <div className="space-y-6">
        <h2 className="text-heading text-2xl text-[#0A0A0A]">Module Guides</h2>
        
        {modules.map((module) => (
          <Card key={module.id} className="border border-[#E5E5EA] shadow-none print:break-inside-avoid">
            <CardHeader className="border-b border-[#E5E5EA] pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className={`w-10 h-10 ${module.bgColor} rounded-lg flex items-center justify-center`}>
                  <module.icon className={`w-5 h-5 ${module.color}`} />
                </div>
                <div>
                  <h3 className="text-heading text-lg text-[#0A0A0A]">{module.title}</h3>
                  <p className="text-sm text-[#0A0A0A]/60 font-normal">{module.description}</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {module.image && (
                <div className="mb-4 rounded-lg overflow-hidden border border-[#E5E5EA]">
                  <img src={module.image} alt={`${module.title} screenshot`} className="w-full h-auto" loading="lazy" />
                  <div className="bg-[#F5F5F7] px-3 py-1.5 text-xs text-[#0A0A0A]/50 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    Navigate to: <span className="font-medium text-[#0A0A0A]/70">{module.title}</span> from the sidebar menu
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {module.steps.map((item, idx) => (
                  <div key={idx}>
                    <div className="flex items-start gap-3 p-3 bg-[#F5F5F7] rounded-lg">
                      <div className={`w-6 h-6 ${module.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-xs font-bold ${module.color}`}>{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A0A0A] text-sm">{item.step}</p>
                        <p className="text-[#0A0A0A]/60 text-sm">{item.description}</p>
                      </div>
                    </div>
                    {item.image && (
                      <div className="ml-9 mt-2 mb-1 rounded-lg overflow-hidden border border-[#E5E5EA]">
                        <img src={item.image} alt={`${item.step} screenshot`} className="w-full h-auto" loading="lazy" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Glossary */}
      <Card className="border border-[#E5E5EA] shadow-none print:break-inside-avoid">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#007AFF]" />
            Glossary of Terms
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-[#0A0A0A]">SKU</p>
              <p className="text-sm text-[#0A0A0A]/60">Stock Keeping Unit - unique product identifier</p>
            </div>
            <div>
              <p className="font-semibold text-[#0A0A0A]">GR (Goods Receipt)</p>
              <p className="text-sm text-[#0A0A0A]/60">Receiving inventory into the system</p>
            </div>
            <div>
              <p className="font-semibold text-[#0A0A0A]">GI (Goods Issue)</p>
              <p className="text-sm text-[#0A0A0A]/60">Removing inventory from the system</p>
            </div>
            <div>
              <p className="font-semibold text-[#0A0A0A]">MAC (Moving Average Cost)</p>
              <p className="text-sm text-[#0A0A0A]/60">Cost calculation method based on weighted average</p>
            </div>
            <div>
              <p className="font-semibold text-[#0A0A0A]">Reorder Level</p>
              <p className="text-sm text-[#0A0A0A]/60">Minimum stock before alert is triggered</p>
            </div>
            <div>
              <p className="font-semibold text-[#0A0A0A]">Cycle Count</p>
              <p className="text-sm text-[#0A0A0A]/60">Physical inventory count for verification</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support */}
      <Card className="border border-[#E5E5EA] shadow-none bg-gradient-to-br from-[#25D366]/5 to-transparent print:hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-heading text-lg text-[#0A0A0A]">Need Help?</h3>
              <p className="text-[#0A0A0A]/60">Contact our support team via WhatsApp for assistance</p>
            </div>
            <a href="https://wa.me/18764507828" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#20BD5A] text-white">
                WhatsApp Support
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
